import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    alpha,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    VpnKey as KeyIcon,
    Code as EndpointsIcon
} from '@mui/icons-material';
import ApiKeys from './ApiKeys';
import ApiEndpoints from './ApiEndpoints';

const ApiManagement: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'keys' | 'endpoints'>('keys');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        <Box sx={{
            bgcolor: '#0D0D0D',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            p: 4,
            borderRadius: '16px'
        }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 1 }}>
                        API Management
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Manage API keys and endpoints for your organization.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{
                        color: '#00E676',
                        borderColor: alpha('#00E676', 0.5),
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#00E676',
                            bgcolor: alpha('#00E676', 0.05),
                        }
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Tab/Toggle Section */}
            <Box sx={{
                display: 'flex',
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                p: 0.5,
                borderRadius: '12px',
                width: 'fit-content',
                border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
                <Button
                    onClick={() => setActiveTab('keys')}
                    startIcon={<KeyIcon />}
                    sx={{
                        px: 3,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        color: activeTab === 'keys' ? '#FFF' : 'rgba(255, 255, 255, 0.4)',
                        bgcolor: activeTab === 'keys' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                        fontWeight: 600,
                        position: 'relative',
                        '&:hover': {
                            bgcolor: activeTab === 'keys' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                        },
                        '&::after': activeTab === 'keys' ? {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '20%',
                            right: '20%',
                            height: '2px',
                            bgcolor: '#10B981',
                            borderRadius: '2px 2px 0 0'
                        } : {}
                    }}
                >
                    API Keys
                </Button>
                <Button
                    onClick={() => setActiveTab('endpoints')}
                    startIcon={<EndpointsIcon />}
                    sx={{
                        px: 3,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        color: activeTab === 'endpoints' ? '#FFF' : 'rgba(255, 255, 255, 0.4)',
                        bgcolor: activeTab === 'endpoints' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                        fontWeight: 600,
                        position: 'relative',
                        '&:hover': {
                            bgcolor: activeTab === 'endpoints' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                        },
                        '&::after': activeTab === 'endpoints' ? {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '20%',
                            right: '20%',
                            height: '2px',
                            bgcolor: '#10B981',
                            borderRadius: '2px 2px 0 0'
                        } : {}
                    }}
                >
                    API Endpoints
                </Button>
            </Box>

            {/* Content Area */}
            <Box sx={{ flexGrow: 1 }}>
                {activeTab === 'keys' ? (
                    <ApiKeys refreshTrigger={refreshTrigger} />
                ) : (
                    <ApiEndpoints refreshTrigger={refreshTrigger} />
                )}
            </Box>
        </Box>
    );
};

export default ApiManagement;
